import React, { useState, useEffect } from 'react';
import {
  Typography,
  Card,
  Select,
  Button,
  Tabs,
  Descriptions,
  Tag,
  Space,
  Spin,
  Alert,
  Upload,
  message
} from 'antd';
import {
  FileTextOutlined,
  RocketOutlined,
  UploadOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import { motion } from 'framer-motion';
import { animations } from '../styles/animations';
import { theme } from '../styles/theme';
import axios from 'axios';
import API_URL from '../config/api';

const { Title, Paragraph, Text } = Typography;
const { TabPane } = Tabs;

const AnalyzePage = () => {
  const [documents, setDocuments] = useState([]);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/documents`);
      setDocuments(response.data.documents || []);
    } catch (error) {
      message.error('Failed to load documents');
    }
  };

  const handleAnalyze = async () => {
    if (!selectedDoc) return;

    setLoading(true);
    try {
      // Fetch extraction
      const extractionRes = await axios.post(
        `${API_URL}/api/documents/${selectedDoc}/analyze`
      );
      setAnalysis(extractionRes.data.extraction);

      // Fetch summary
      const summaryRes = await axios.post(
        `${API_URL}/api/documents/${selectedDoc}/summarize`
      );
      setSummary(summaryRes.data.summary);

      message.success('Analysis complete!');
    } catch (error) {
      message.error('Analysis failed. Please try again.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (file) => {
    setUploading(true);
    const formData = new FormData();
    formData.append('document', file);

    try {
      const response = await axios.post(`${API_URL}/api/documents/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      message.success(`${file.name} uploaded successfully`);
      await fetchDocuments();

      // Auto-select the newly uploaded document
      if (response.data && response.data.documentId) {
        setSelectedDoc(response.data.documentId);
      }
    } catch (error) {
      console.error('Upload error:', error);
      const errorMsg = error.response?.data?.error || error.response?.data?.message || error.message;
      message.error(`Upload failed: ${errorMsg}`);
    } finally {
      setUploading(false);
    }

    return false; // Prevent default upload behavior
  };

  const uploadProps = {
    name: 'document',
    accept: '.pdf',
    showUploadList: false,
    beforeUpload: handleUpload,
    customRequest: () => {}, // Disable default request since we handle it in beforeUpload
  };

  return (
    <motion.div {...animations.fadeIn}>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>
          <FileTextOutlined /> Analyze Document
        </Title>
        <Paragraph type="secondary">
          Extract key terms and generate plain-English summaries from loan agreements.
        </Paragraph>
      </div>

      {/* Document Selection */}
      <Card style={{ marginBottom: '24px' }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <Text strong style={{ display: 'block', marginBottom: '8px' }}>
              Select Document
            </Text>
            <Space>
              <Select
                style={{ width: 400 }}
                placeholder="Choose a document to analyze"
                value={selectedDoc}
                onChange={setSelectedDoc}
                options={documents.map(doc => ({
                  value: doc.id,
                  label: (
                    <Space>
                      {doc.filename}
                      {doc.is_sample && <Tag color="blue">Sample</Tag>}
                    </Space>
                  )
                }))}
              />
              <Button
                type="primary"
                icon={<RocketOutlined />}
                onClick={handleAnalyze}
                disabled={!selectedDoc}
                loading={loading}
              >
                Analyze
              </Button>
            </Space>
          </div>

          <div>
            <Text strong style={{ display: 'block', marginBottom: '8px' }}>
              Or Upload New Document
            </Text>
            <Upload {...uploadProps}>
              <Button icon={<UploadOutlined />} loading={uploading}>
                Upload PDF (Max 2 new documents)
              </Button>
            </Upload>
          </div>
        </Space>
      </Card>

      {/* Results */}
      {loading && (
        <Card>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" />
            <Title level={4} style={{ marginTop: '16px' }}>
              Analyzing document...
            </Title>
            <Text type="secondary">AI is extracting key information</Text>
          </div>
        </Card>
      )}

      {!loading && (analysis || summary) && (
        <motion.div {...animations.slideUp}>
          <Alert
            message="Analysis Complete"
            description="AI has successfully extracted key terms and generated a summary."
            type="success"
            icon={<CheckCircleOutlined />}
            showIcon
            style={{ marginBottom: '24px' }}
          />

          <Card>
            <Tabs defaultActiveKey="extraction">
              <TabPane tab="Key Terms" key="extraction">
                {analysis && (
                  <Descriptions bordered column={1}>
                    <Descriptions.Item label="Borrower">
                      <Text strong>{analysis.borrower}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Lender">
                      <Text strong>{analysis.lender}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Loan Amount">
                      <Tag color="blue" style={{ fontSize: '14px' }}>
                        {analysis.loanAmount}
                      </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Interest Rate">
                      <Tag color="green">{analysis.interestRate}</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Maturity Date">
                      {analysis.maturityDate}
                    </Descriptions.Item>
                    <Descriptions.Item label="Purpose">
                      {analysis.purpose}
                    </Descriptions.Item>
                    <Descriptions.Item label="Key Covenants">
                      <ul style={{ margin: 0, paddingLeft: '20px' }}>
                        {analysis.keyCovenants.map((covenant, idx) => (
                          <li key={idx}>{covenant}</li>
                        ))}
                      </ul>
                    </Descriptions.Item>
                    <Descriptions.Item label="Security Interest">
                      {analysis.securityInterest}
                    </Descriptions.Item>
                    <Descriptions.Item label="Governing Law">
                      {analysis.governingLaw}
                    </Descriptions.Item>
                  </Descriptions>
                )}
              </TabPane>

              <TabPane tab="Plain-English Summary" key="summary">
                {summary && (
                  <div style={{ fontSize: '15px', lineHeight: '1.8' }}>
                    {summary.split('\n\n').map((paragraph, idx) => (
                      <Paragraph key={idx}>{paragraph}</Paragraph>
                    ))}
                  </div>
                )}
              </TabPane>
            </Tabs>
          </Card>
        </motion.div>
      )}
    </motion.div>
  );
};

export default AnalyzePage;
